import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaChartLine,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const API_BASE = "http://nissi-india.com/api";

const RateComparisonReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [quotationNo, setQuotationNo] = useState("");
  const [purReqNo, setPurReqNo] = useState("");
  const [store, setStore] = useState("");

  // Dropdown / Data
  const [storeOptions, setStoreOptions] = useState([]);
  const [results, setResults] = useState([]);

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = (localStorage.getItem("branchType") || "").toUpperCase(); // HO / BRANCH
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const initialBranchType = branch === "HO" ? "HO" : "Branch";
  const [branchType, setBranchType] = useState(initialBranchType);
  const [location, setLocation] = useState(initialBranchType === "HO" ? "HO" : "BRANCH");

  useEffect(() => {
    setBranchType(initialBranchType);
    setLocation(initialBranchType === "HO" ? "HO" : "BRANCH");
  }, [initialBranchType]);

  const handleBranchTypeChange = (value) => {
    setBranchType(value);
    setLocation(value === "HO" ? "HO" : "BRANCH");
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate(new Date().toISOString().split("T")[0]);
    setQuotationNo("");
    setPurReqNo("");
    setStore("");
    setResults([]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? dateStr : date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const getStatusBadgeClass = (status) => {
    if (status === "Approved") {
      return isDark
        ? "bg-green-900 text-green-200"
        : "bg-green-100 text-green-800";
    }

    if (status === "Rejected") {
      return isDark ? "bg-red-900 text-red-200" : "bg-red-100 text-red-800";
    }

    return isDark
      ? "bg-yellow-900 text-yellow-200"
      : "bg-yellow-100 text-yellow-800";
  };

  const handleCompare = (item) => {
    const id =
      item?.fld_quotationid ||
      item?.fld_ratecomparisonid ||
      item?.fld_enquiryid ||
      item?.fld_id;

    if (!id) {
      toast.warn("Invalid comparison id");
      return;
    }

    navigate(`/inventory/RateComparisonReport_View?id=${id}`);
  };

  // Add New
  const handleAddNew = () => {
    navigate("/ratecomparison-add");
  };

  // Load Branch / Store options
  useEffect(() => {
    const fetchStoreOptions = async () => {
      try {
        const res = await axios.post(
          `${API_BASE}/getFromStoreOptions`,
          {
            branchtype: branch || "HO",
            branchid: branchId || 8,
          },
          {
            headers: commonHeaders,
          }
        );

        setStoreOptions(res.data.storeOptions || []);
      } catch (error) {
        toast.error("Failed to load branch options");
      }
    };

    fetchStoreOptions();
  }, [branch, branchId, commonHeaders]);

  const handleSearch = async () => {
    if (!store) {
      toast.warn("Please select a Branch");
      return;
    }

    const params = {
      branchid: store || branchId || "8",
      branchwiseno: quotationNo || "",
      fromdate: fromDate || "",
      todate: toDate || "",
      purreqno: purReqNo || "",
      btype: location || "BRANCH",
    };

    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/getquotationdetails_rpt`, {
        params,
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setResults(data);
      toast.success(`Loaded ${data.length} record(s)`);
    } catch (err) {
      toast.error("Failed to fetch rate comparison report");
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/getquotationdetails_rpt`, {
        params: {
          branchid: branchId || "8",
          branchwiseno: "",
          fromdate: "",
          todate: "",
          purreqno: "",
          btype: location || "BRANCH",
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setResults(data);
      toast.success("All records loaded");
    } catch (err) {
      toast.error("Failed to fetch all rate comparison records");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="RATE COMPARISON REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Rate Comparison Report
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {results.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700" +
                    (showFilters ? " bg-gray-700" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-100" +
                    (showFilters ? " bg-gray-100" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>

            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg transition-colors`}
            >
              <FaPlus className="w-4 h-4" />
              Add New
            </button>
          </div>
        </div>

        {/* Branch Type */}
        <div className="mb-6">
          <label
            className={`block text-sm font-medium mb-3 ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Branch Type
          </label>

          <div className="flex gap-2">
            <button
              onClick={() => handleBranchTypeChange("HO")}
              disabled={branch !== "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors
                ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white`
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
                ${branch !== "HO" ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Head Office
            </button>

            <button
              onClick={() => handleBranchTypeChange("Branch")}
              disabled={branch === "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors
                ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
                ${branch === "HO" ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Branch
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Search Filters
              </h3>

              <button
                onClick={clearFilters}
                className={`text-sm flex items-center gap-2 ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <FaTimes className="w-3 h-3" />
                Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Quotation No
                </label>
                <input
                  type="text"
                  value={quotationNo}
                  onChange={(e) => setQuotationNo(e.target.value)}
                  placeholder="Enter quotation number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Purchase Req No
                </label>
                <input
                  type="text"
                  value={purReqNo}
                  onChange={(e) => setPurReqNo(e.target.value)}
                  placeholder="Enter purchase req number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Branch <span className="text-red-500">*</span>
                </label>
                <select
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select Branch</option>
                  {storeOptions.map((s) => (
                    <option key={s.fld_storeid} value={s.fld_storeid}>
                      {s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className={`flex justify-between items-center pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <button
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
                >
                  <FaSearch className="w-4 h-4" />
                  Search
                </button>

                <button
                  onClick={handleViewAll}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
                >
                  <FaSearch className="w-4 h-4" />
                  View All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : results.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="p-4 border-b border-gray-200">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Rate Comparison Results ({results.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Sl No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Quotation No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Purchase Req No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Compare
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y divide-gray-200 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  {results.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      }`}
                    >
                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {idx + 1}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {item.fld_quotationno || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {formatDate(item.fld_date)}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {item.fld_purchasereqno || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {item.fld_suppliername || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {formatCurrency(item.fld_totalamount)}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(
                            item.fld_status || "Pending"
                          )}`}
                        >
                          {item.fld_status || "Pending"}
                        </span>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleCompare(item)}
                          className={`p-2 rounded transition-colors ${
                            isDark
                              ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                              : "text-blue-600 hover:text-blue-800 hover:bg-gray-100"
                          }`}
                          title="View Comparison"
                        >
                          <FaChartLine className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div
              className={`text-center py-12 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p>
                No records found. Please use the filters to search for rate
                comparison records.
              </p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default RateComparisonReport;